import { derived, readable, type Readable } from "svelte/store";
import { get } from "lodash-es";
import {
  type Maybe,
  type Place,
  type EventHours,
  type Map,
  Status,
  type MapTextbox,
  type MapWaypoint,
  type Schedule
} from "../types";
import { getStatus } from "./status";
import { currentEventLines, type Waypoint } from "../support/connections";
import { boundingBox, type Rect } from "../support/geometry";

export const FABRIC_JS_LINE_HEIGHT = 1.16;

export const getActiveWaypoints = function (
  waypoints: Array<Waypoint>,
  placeId: string | null,
  waypointId: string | null
): Array<Waypoint> {
  const key = placeId == null ? "id" : "placeId";
  const id = placeId == null ? waypointId : placeId;

  return waypoints.filter((w) => w[key] == id);
};

export const getPathways$ = function (
  location: Readable<Waypoint>,
  waypoints: Array<Waypoint>,
  placeId: string | null,
  waypointId: string | null
) {
  if (location == null) {
    return readable({ withoutHazards: [], withHazards: [] });
  }

  const activeWaypoints = getActiveWaypoints(waypoints, placeId, waypointId);

  return derived([location], ([$location]) => {
    return currentEventLines(waypoints, $location, activeWaypoints);
  });
};

export function mapTextBoxRect(textBox: MapTextbox): Rect {
  return {
    x: textBox.x,
    y: textBox.y,
    height: textBox.fontSize * FABRIC_JS_LINE_HEIGHT /* comes from fabricjs textboxes */,
    width: textBox.width
  } as Rect;
}

export const getFocusBox = function (map: Map, placeId: string | null): Rect | null {
  if (placeId == null) {
    return null;
  }

  const textboxes = get(map, "textboxes", []);
  const rects = textboxes.filter((t) => t.placeId == placeId).map(mapTextBoxRect);

  return boundingBox(rects);
};

export function getFocusedEvents(schedule: Schedule, eventId: string | null): EventHours[] {
  if (eventId == null) {
    return [];
  }

  return schedule.eventHours.filter((h) => h.eventId == eventId);
}

export function isDisplayableStatus(status: Status): boolean {
  switch (status) {
    case Status.ACTIVE:
    case Status.CLOSED_TEMPORARILY:
    case Status.ON_HOLD:
    case Status.OUT_LATE:
      return true;
    default:
      return false;
  }
}

export function isDisplayablePlace(place: Place, time: Date): boolean {
  return isDisplayableStatus(getStatus(place, time));
}

export function getPlace(map: Maybe<Map>, places: Place[], placeId: string | null) {
  if (map == null) {
    return;
  }

  const textboxes: Array<MapTextbox | MapWaypoint> = get(map, "textboxes", []);
  const waypoints: Array<MapTextbox | MapWaypoint> = get(map, "waypoints", []);
  const shapes = textboxes.concat(waypoints) as Array<MapTextbox | MapWaypoint>;
  const validPlaceIds = new Set(shapes.map((i) => i.placeId));

  if (validPlaceIds.has(placeId) == false) {
    return null;
  }

  return places.find((p) => p.id == placeId);
}

export function displayOnMap(
  map: Map,
  places: Place[],
  placeId: string | null,
  time: Date
): boolean {
  if (placeId == null) {
    // Place was not found, assume no relationship with textbox
    return true;
  }

  const place = getPlace(map, places, placeId) as Place;

  if (place == null) {
    return false;
  }

  const status = getStatus(place, time);

  return isDisplayableStatus(status);
}

export function isVisible(canvasZoomLevel: number, zoomMin: number, zoomMax: number): boolean {
  // Canvas zoom level is float
  const zoom = canvasZoomLevel * 100;

  const aboveMin = zoomMin == null || zoom >= zoomMin;
  const belowMax = zoomMax == null || zoom <= zoomMax;

  return aboveMin && belowMax;
}
